import React, { useState } from 'react';
import { Card, Typography, Space, Button } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import { useTheme } from '@/components/common/ThemeProvider';
import { NubUserList } from '../components/NubUserList';
import { NubUserFormDialog } from '../components/NubUserFormDialog';
import { NubUserFilters } from '../components/NubUserFilters';
import { useNubUser } from '../hooks/use-nub-user';
import { NubUserDTO } from '../types';
import { PasswordConfigDialog, PasswordConfig } from '../components/PasswordConfigDialog';
import { useMessage } from '@/shared/hooks/useMessage';

const { Title } = Typography;

export const NubUserManagement: React.FC = () => {
    const { dark } = useTheme();
    const message = useMessage();
    const {
        users,
        activeUser,
        groups,
        accessLevels,
        actionMode,
        isModalOpen,
        isLoading,
        handleUserAction,
        setModalOpen,
        createUser,
        updateUser,
        refetchUsers,
        passwordConfig,
        updatePasswordConfig,
        handleFiltersChange,
        clearFilters,
        currentFilters
    } = useNubUser();

    const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);

    const handleFormSubmit = async (formData: NubUserDTO) => {
        try {
            if (actionMode === 'add') {
                await createUser(formData);
                message.success('User created successfully');
            } else if (actionMode === 'edit' && activeUser) {
                // @ts-ignore
                await updateUser(activeUser.userId, formData);
                message.success('User updated successfully');
            }
            setModalOpen(false);
        } catch (error) {
            console.error('Operation failed:', error);
            message.error('Operation failed. Please try again.');
        }
    };

    const handleConfigSave = async (config: PasswordConfig) => {
        try {
            await updatePasswordConfig(config);
            message.success('Password configuration updated successfully');
            setIsConfigModalOpen(false);
        } catch (error) {
            console.error('Failed to update password config:', error);
            message.error('Failed to update password configuration');
        }
    };

    const handleResetPassword = async (userId: string) => {
        try {
            const user = users.find(u => u.userId === userId);
            if (!user) return;

            const resetData = {
                ...user,
                resetPassword: true
            };

            await updateUser(userId, resetData);
            message.success('Password has been reset successfully');
        } catch (error) {
            console.error('Password reset failed:', error);
            message.error('Failed to reset password');
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <Title level={2} className={dark ? 'text-white' : ''}>
                    User Management
                </Title>

                <Space>
                    <Button
                        icon={<SettingOutlined />}
                        onClick={() => setIsConfigModalOpen(true)}
                    >
                        Password Settings
                    </Button>
                </Space>
            </div>

            <NubUserFilters
                groups={groups}
                onFiltersChange={handleFiltersChange}
                loading={isLoading}
            />

            <Card className={dark ? 'bg-gray-800' : ''} loading={isLoading}>
                <NubUserList
                    users={users}
                    onUserAction={handleUserAction}
                    onResetPassword={handleResetPassword}
                />

                <NubUserFormDialog
                    open={isModalOpen}
                    mode={actionMode}
                    initialData={activeUser}
                    groups={groups}
                    accessLevels={accessLevels}
                    onCancel={() => setModalOpen(false)}
                    onSubmit={handleFormSubmit}
                    passwordConfig={passwordConfig}
                />

                <PasswordConfigDialog
                    open={isConfigModalOpen}
                    onCancel={() => setIsConfigModalOpen(false)}
                    onSave={handleConfigSave}
                    initialConfig={passwordConfig}
                />
            </Card>
        </div>
    );
};